import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { askAiDesk, getLead, listBuyerMessages, listTrackedLinks, simulateInbound } from '../lib/api';
import { dateTime, friendlyError } from '../lib/format';
import { prefilledText } from '../lib/links';
import type { Message, TrackedLink } from '../lib/types';
import { Button, ErrorText, Notice, PageHeader, SelectField, TextField } from '../components/ui';

function randomTestPhone() {
  return `9${Math.floor(100000000 + Math.random() * 899999999)}`;
}

export function Simulator() {
  const { projects, members } = useAuth();
  const [params] = useSearchParams();
  const [phone, setPhone] = useState(randomTestPhone);
  const [name, setName] = useState('Test Buyer');
  const [text, setText] = useState('');
  const [links, setLinks] = useState<TrackedLink[]>([]);
  const [buyerId, setBuyerId] = useState<string | null>(null);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const end = useRef<HTMLDivElement>(null);

  const projectName = (id: string | null) => projects.find((p) => p.id === id)?.name ?? null;
  const memberName = (id: string | null) => members.find((m) => m.id === id)?.full_name ?? 'no rep';

  useEffect(() => {
    listTrackedLinks().then((ls) => {
      setLinks(ls);
      const code = params.get('code');
      const l = ls.find((x) => x.code === code);
      if (l) setText(prefilledText(projectName(l.project_id), l.code));
    }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!buyerId) return;
    const ms = await listBuyerMessages([buyerId]).catch(() => null);
    if (ms) setMessages(ms);
  }, [buyerId]);
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: 'nearest' }); }, [messages.length]);

  function newBuyer() {
    setPhone(randomTestPhone()); setBuyerId(null); setLeadId(null); setMessages([]); setNotice(''); setError('');
  }

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const r = await simulateInbound(phone, name, text);
      const lead = await getLead(r.lead_id);
      setBuyerId(lead?.buyer_id ?? null);
      setLeadId(r.lead_id);
      const base = r.is_new
        ? `New test lead L-${lead?.ref_no} created${r.code ? ` from link ${r.code}` : ' as "WhatsApp, direct"'}.`
        : `Added to lead L-${lead?.ref_no}.`;
      setNotice(`${base} AI desk is replying…`);
      setText('');
      await load();
      const ai = await askAiDesk(r.message_id);
      const lane = ai.detail?.lane;
      setNotice(`${base} ${ai.outcome === 'recorded'
        ? lane === 'green' ? 'AI desk replied.' : lane === 'amber' ? 'AI reply held for review (unapproved number); the buyer got a holding message.' : 'AI reply blocked (risky claim); the buyer got a holding message and the lead needs a person.'
        : ai.outcome === 'skipped' ? 'AI desk stayed quiet (switched off, or a person is already chatting).'
        : `AI desk could not reply: ${ai.detail ?? 'unknown'}. The lead is marked "Needs you".`}`);
      await load();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHeader title="WhatsApp simulator"
        subtitle="Type as a buyer. Everything created here is marked Test and kept out of real reports." />
      <div className="grid gap-6 lg:grid-cols-[400px_minmax(0,1fr)]">
        <div className="mx-auto w-full max-w-[400px] overflow-hidden rounded-[28px] border-2 border-ink bg-surface shadow-xl">
          <div className="flex items-center justify-between bg-ok px-4 py-3 text-surface">
            <div>
              <p className="text-sm font-semibold">Your firm's desk</p>
              <p className="text-xs opacity-80">Buyer: {name || 'unnamed'} · {phone}</p>
            </div>
            <button onClick={newBuyer} className="rounded-md px-2 py-1 text-xs font-medium hover:bg-scrim">New buyer</button>
          </div>
          <div className="flex h-[420px] flex-col gap-2 overflow-y-auto bg-surface2 p-3">
            {messages.length === 0 && <p className="m-auto max-w-[260px] text-center text-sm text-muted">Send a message as the buyer. Pick a tracked link to see how a post brings in a lead.</p>}
            {messages.map((m) => (
              <div key={m.id} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.direction === 'in' ? 'self-end bg-okSoft' : 'self-start bg-surface'}`}>
                <p className="whitespace-pre-wrap">{m.body}</p>
                <p className="mt-0.5 text-[11px] text-muted">{dateTime(m.created_at)}</p>
              </div>
            ))}
            <div ref={end} />
          </div>
          <form onSubmit={send} className="flex gap-2 border-t border-line p-3">
            <input aria-label="Message as the buyer" value={text} onChange={(e) => setText(e.target.value)} placeholder="Message"
              className="min-w-0 flex-1 rounded-full border border-line px-4 py-2 text-sm outline-none focus:border-accent" />
            <Button type="submit" variant="primary" disabled={busy || !text.trim()} className="rounded-full">Send</Button>
          </form>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-5">
            <h2 className="font-display text-lg">Buyer</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField label="Phone" value={phone} onChange={(e) => { setPhone(e.target.value); setBuyerId(null); setMessages([]); }} />
              <TextField label="WhatsApp name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <SelectField label="Start from a tracked link" value="" onChange={(e) => {
              const l = links.find((x) => x.code === e.target.value);
              if (l) setText(prefilledText(projectName(l.project_id), l.code));
            }}>
              <option value="">Choose a link to fill in the buyer's first message</option>
              {links.filter((l) => l.active).map((l) => (
                <option key={l.id} value={l.code}>{l.code} · {projectName(l.project_id) ?? 'Any project'} · {l.channel_label ?? 'Post'} · {memberName(l.member_id)}</option>
              ))}
            </SelectField>
            {links.length === 0 && <p className="text-sm text-muted">No tracked links yet. <Link className="text-accent hover:underline" to="/links">Make one</Link> to test post attribution.</p>}
          </div>
          {notice && <Notice>{notice} {leadId && <Link className="font-medium underline" to={`/leads?lead=${leadId}`}>Open the lead</Link>}</Notice>}
          <ErrorText>{error}</ErrorText>
          <p className="text-sm text-muted">The AI desk answers each message using the project's approved facts. To reply as a person, open the lead and use its WhatsApp box; the AI then steps back for 2 hours.</p>
        </div>
      </div>
    </div>
  );
}
